import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import MouseIcon from '@mui/icons-material/Mouse';
import { alpha } from '@mui/material/styles';


// Header background slideshow
export default function Header() {
  const theme = useTheme();
  const [images, setImages] = React.useState([]); // preloaded valid image URLs
  const [index, setIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const [announce, setAnnounce] = React.useState('');

  // Candidate filenames to try in /public.
  // Keep this list small and aligned with assets actually shipped in `public/` to avoid many 404s.
  const candidates = React.useMemo(() => [
    '/header-1.jpg',
    '/header-2.jpg',
    '/header-3.jpg',
    '/header-4.jpg'
  ], []);

  // Check whether a site-root video exists before rendering it (avoids a direct <video> request
  // that would generate a 404 in the browser console if missing). We use a HEAD request.
  const [videoExists, setVideoExists] = React.useState(false);
  React.useEffect(() => {
    let mounted = true;
    const check = async () => {
      try {
        const resp = await fetch('/brand-bg.mp4', { method: 'HEAD' });
        if (mounted && resp.ok) setVideoExists(true);
      } catch (err) {
        // ignore - leave videoExists false
      }
    };
    check();
    return () => { mounted = false; };
  }, []);

  // Preload images that succeed (avoid 404s)
  React.useEffect(() => {
    let mounted = true;
    const loaded = [];
    let attempts = 0;
    const tryNext = (i) => {
      if (!mounted) return;
      if (i >= candidates.length) {
        if (mounted) setImages(loaded);
        return;
      }
      const url = candidates[i];
      const img = new Image();
      img.onload = () => {
        loaded.push(url);
        // continue checking next candidate
        tryNext(i + 1);
      };
      img.onerror = () => {
        // skip
        tryNext(i + 1);
      };
      // start loading
      img.src = url;
      attempts++;
    };
    tryNext(0);
    return () => { mounted = false; };
  }, [candidates]);

  // Autoplay interval
  React.useEffect(() => {
    if (images.length <= 1 || isPaused) return undefined;
    const id = setInterval(() => setIndex(i => (i + 1) % images.length), 5000);
    return () => clearInterval(id);
  }, [images, isPaused]);

  // Announce image change for screen readers
  React.useEffect(() => {
    if (!images || images.length === 0) return;
    setAnnounce(`Header background ${index + 1} of ${images.length}`);
  }, [index, images]);

  const handleFocusIn = () => setIsPaused(true);
  const handleFocusOut = () => setIsPaused(false);

  return (
    <Box
      component="header"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: theme.palette.common.white,
        minHeight: { xs: 180, md: 240 },
        py: { xs: 3, md: 6 },
        textAlign: 'center',
        backgroundColor: theme.palette.primary.main,
      }}
      aria-label="Neha Info Tech header"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Optional video background: render only if `/brand-bg.mp4` exists in the site root. */}
      {videoExists && (
        <Box component="video" autoPlay muted loop playsInline sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
          <source src="/brand-bg.mp4" type="video/mp4" />
        </Box>
      )}

      {/* Image slideshow layers (if images detected). If none, fallback to SVG background. */}
      {images && images.length > 0 ? (
        images.map((url, i) => (
          <Box
            key={url}
            role="img"
            aria-hidden={i === index ? 'false' : 'true'}
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `linear-gradient(rgba(0,0,0,0.18), rgba(0,0,0,0.18)), url(${url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transition: 'opacity 900ms ease',
              opacity: i === index ? 1 : 0,
              zIndex: 0,
            }}
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
          />
        ))
      ) : (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.18), rgba(0,0,0,0.18)), url('/brand-bg.svg'), url('https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1600&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
          aria-hidden="true"
        />
      )}

      {/* ARIA live region */}
      <Box component="div" aria-live="polite" aria-atomic="true" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)', whiteSpace: 'nowrap' }}>{announce}</Box>

      {/* Creative top-left brand block (left-only; header.js) */}
      <Box
        component="div"
        role="banner"
        sx={(theme) => ({
          position: 'absolute',
          top: { xs: 14, md: 28 },
          left: { xs: 14, md: 28 },
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: alpha(theme.palette.common.black, 0.32),
          color: theme.palette.common.white,
          px: { xs: 1.5, md: 2.25 },
          py: { xs: 0.75, md: 1 },
          borderRadius: 3,
          boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.5)}`,
          backdropFilter: 'blur(6px)',
          transition: 'transform 220ms ease, box-shadow 220ms ease',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 18px 36px ${alpha(theme.palette.common.black, 0.55)}` }
        })}
        aria-label="NehaInfoTech brand"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, lineHeight: 1.02 }}>
          <MouseIcon sx={{ fontSize: 28, color: 'primary.light', mr: 0.5 }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', md: '1.15rem' } }}>{'Neha Info Tech'}</Typography>
            <Typography sx={{ fontSize: { xs: '0.76rem', md: '0.9rem' }, opacity: 0.95 }}>{'Innovative Software Solutions'}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
